import * as React from 'react';
import {
    Card, CardContent, Typography, Grid, Box, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
    CircularProgress, Alert, IconButton, Menu, MenuItem, FormControlLabel,
    Checkbox, Tooltip, Badge, TextField, InputAdornment, Switch,
    FormControl, InputLabel, Select, Divider, Avatar, TablePagination
} from '@mui/material';
import { useNotify } from 'react-admin';
import SportsIcon from '@mui/icons-material/Sports';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import GetAppIcon from '@mui/icons-material/GetApp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ScoreIcon from '@mui/icons-material/Score';
import ClearIcon from '@mui/icons-material/Clear';
import HomeIcon from '@mui/icons-material/Home';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../../config/config.json';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

export const TeamMatches = ({ teamId, teamName }) => {
    const notify = useNotify();

    // Stati principali
    const [matches, setMatches] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [autoRefresh, setAutoRefresh] = React.useState(false);
    const [refreshInterval, setRefreshInterval] = React.useState(null);

    // Filtri base
    const [filterStatus, setFilterStatus] = React.useState('all');
    const [filterType, setFilterType] = React.useState('all'); // home, away, all

    // NUOVI FILTRI CASCADE
    const [filterLega, setFilterLega] = React.useState('all');
    const [filterStagione, setFilterStagione] = React.useState('all');
    const [filterGiornata, setFilterGiornata] = React.useState('all');

    // Dropdown data per filtri cascade
    const [leghe, setLeghe] = React.useState([]);
    const [stagioni, setStagioni] = React.useState([]);
    const [giornate, setGiornate] = React.useState([]);

    // Loading states per dropdown
    const [loadingStagioni, setLoadingStagioni] = React.useState(false);
    const [loadingGiornate, setLoadingGiornate] = React.useState(false);

    // Paginazione
    const [page, setPage] = React.useState(0);
    const [limit, setLimit] = React.useState(25);
    const [total, setTotal] = React.useState(0);

    const [visibleColumns, setVisibleColumns] = React.useState(() => {
        const saved = localStorage.getItem('team-matches-visible-columns');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return parsed.filter(col => Object.keys(COLUMN_CONFIG).includes(col));
            } catch (e) {
                console.warn('Errore nel parsing delle colonne salvate:', e);
            }
        }
        return ['data', 'avversario', 'casa_trasferta', 'risultato', 'stato', 'lega', 'stagione'];
    });

    const [anchorEl, setAnchorEl] = React.useState(null);
    const [sortConfig, setSortConfig] = React.useState({ key: 'data', direction: 'desc' });

    // Configurazione delle colonne (aggiornata con nuove colonne)
    const COLUMN_CONFIG = {
        data: {
            label: 'Data',
            key: 'data',
            align: 'center',
            default: true,
            sortable: true,
            width: 120,
            render: (value) => (
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {value ? new Date(value).toLocaleDateString('it-IT') : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {value ? new Date(value).toLocaleTimeString('it-IT', {
                            hour: '2-digit',
                            minute: '2-digit'
                        }) : ''}
                    </Typography>
                </Box>
            )
        },
        avversario: {
            label: 'Avversario',
            key: 'avversario',
            align: 'left',
            default: true,
            sortable: true,
            width: 200,
            render: (value, row) => {
                const isHome = row.teamCasaId === parseInt(teamId);
                const opponent = isHome ? row.teamOspiteNome : row.teamCasaNome;
                const opponentLogo = isHome ? row.teamOspiteFoto : row.teamCasaFoto;

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                            src={opponentLogo}
                            alt={opponent}
                            sx={{
                                width: 32,
                                height: 32,
                                bgcolor: opponentLogo ? 'transparent' : 'primary.main'
                            }}
                        >
                            {!opponentLogo && opponent ? opponent.charAt(0).toUpperCase() : '⚽'}
                        </Avatar>
                        <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {opponent || 'Squadra N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {isHome ? 'vs' : '@'} {opponent}
                            </Typography>
                        </Box>
                    </Box>
                );
            }
        },
        casa_trasferta: {
            label: 'Casa/Trasferta',
            key: 'casa_trasferta',
            align: 'center',
            default: true,
            sortable: false,
            width: 120,
            render: (value, row) => {
                const isHome = row.teamCasaId === parseInt(teamId);
                return (
                    <Chip
                        label={isHome ? 'Casa' : 'Trasferta'}
                        size="small"
                        icon={isHome ? <HomeIcon sx={{ fontSize: 16 }} /> : <FlightTakeoffIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            bgcolor: isHome ? '#e8f5e8' : '#fff3e0',
                            color: isHome ? '#2e7d32' : '#e65100',
                            fontWeight: 500
                        }}
                    />
                );
            }
        },
        risultato: {
            label: 'Risultato',
            key: 'risultato',
            align: 'center',
            default: true,
            sortable: false,
            width: 100,
            render: (value, row) => {
                const isHome = row.teamCasaId === parseInt(teamId);
                const myScore = isHome ? row.goalCasa : row.goalOspite;
                const opponentScore = isHome ? row.goalOspite : row.goalCasa;

                if (myScore !== null && opponentScore !== null) {
                    const isWin = myScore > opponentScore;
                    const isDraw = myScore === opponentScore;

                    return (
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 700,
                                    color: isWin ? '#4caf50' : isDraw ? '#ff9800' : '#f44336'
                                }}
                            >
                                {myScore} - {opponentScore}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: isWin ? '#4caf50' : isDraw ? '#ff9800' : '#f44336',
                                    fontWeight: 500
                                }}
                            >
                                {isWin ? 'V' : isDraw ? 'P' : 'S'}
                            </Typography>
                        </Box>
                    );
                }

                return (
                    <Typography variant="body2" color="text.secondary">
                        -
                    </Typography>
                );
            }
        },
        stato: {
            label: 'Stato',
            key: 'stato',
            align: 'center',
            default: true,
            sortable: true,
            width: 120,
            render: (value, row) => {
                let status = 'Programmata';
                let color = '#2196f3';
                let bgcolor = '#e3f2fd';

                if (row.flFinita) {
                    status = 'Terminata';
                    color = '#4caf50';
                    bgcolor = '#e8f5e8';
                } else if (row.flInCorso) {
                    status = 'In Corso';
                    color = '#ff9800';
                    bgcolor = '#fff3e0';
                } else if (row.flAnnullata) {
                    status = 'Annullata';
                    color = '#f44336';
                    bgcolor = '#ffebee';
                }

                return (
                    <Chip
                        label={status}
                        size="small"
                        sx={{ color, bgcolor, fontWeight: 500 }}
                    />
                );
            }
        },
        // NUOVE COLONNE per filtri
        lega: {
            label: 'Lega',
            key: 'lega',
            align: 'center',
            default: false,
            sortable: true,
            width: 120,
            render: (value, row) => (
                <Chip
                    label={row.legaNome || `Lega ${row.legaId}`}
                    size="small"
                    sx={{
                        bgcolor: '#fff3e0',
                        color: '#e65100',
                        fontWeight: 500
                    }}
                    icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />}
                />
            )
        },
        stagione: {
            label: 'Stagione',
            key: 'stagione',
            align: 'center',
            default: false,
            sortable: true,
            width: 120,
            render: (value, row) => (
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {row.stagioneNome || `Stagione ${row.stagioneId}`}
                    </Typography>
                </Box>
            )
        },
        giornataNumero: {
            label: 'Giornata',
            key: 'giornataNumero',
            align: 'center',
            default: false,
            sortable: true,
            width: 100,
            render: (value, row) => (
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.giornataNumero || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {row.giornataNome || ''}
                    </Typography>
                </Box>
            )
        },
        luogo: {
            label: 'Luogo',
            key: 'luogo',
            align: 'left',
            default: false,
            sortable: true,
            width: 150,
            render: (value) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">
                        {value || 'Non specificato'}
                    </Typography>
                </Box>
            )
        },
        note: {
            label: 'Note',
            key: 'note',
            align: 'left',
            default: false,
            sortable: false,
            width: 200,
            render: (value) => (
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    {value && value.length > 50 ? `${value.substring(0, 50)}...` : value || '-'}
                </Typography>
            )
        }
    };

    // NUOVI HANDLERS per filtri CASCADE
    const handleLegaChange = async (value) => {
        setFilterLega(value);

        // Reset filtri dipendenti
        setFilterStagione('all');
        setFilterGiornata('all');
        setStagioni([]);
        setGiornate([]);
        setPage(0);

        // Se ho selezionato una lega specifica, carico le stagioni
        if (value !== 'all') {
            await loadStagioniByLega(value);
        }
    };

    const handleStagioneChange = async (value) => {
        setFilterStagione(value);

        // Reset filtro dipendente
        setFilterGiornata('all');
        setGiornate([]);
        setPage(0);

        // Se ho selezionato una stagione specifica e una lega, carico le giornate
        if (value !== 'all' && filterLega !== 'all') {
            await loadGiornateByLegaStagione(filterLega, value);
        }
    };

    const handleGiornataChange = (value) => {
        setFilterGiornata(value);
        setPage(0);
    };

    // NUOVE API CALLS per filtri CASCADE
    const loadLeghe = async () => {
        try {
            const jwt = getJwt();
            const response = await axios.get(`${host}/tbl-lega`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    $select: ['id', 'nome'],
                    flVisibile: 1,
                    flAttivo: 1,
                    $sort: { nome: 1 }
                }
            });
            setLeghe(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento leghe:", error);
            notify("Errore nel caricamento leghe", { type: 'error' });
        }
    };

    const loadStagioniByLega = async (legaId) => {
        try {
            setLoadingStagioni(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/proc-get-stagione-elenco`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    p_input_legaId_dal: legaId,
                    p_input_legaId_al: legaId
                }
            });

            const data = response.data;
            const stagioniArray = Array.isArray(data.data) ? data.data :
                Array.isArray(data) ? data : [];

            setStagioni(stagioniArray);
        } catch (error) {
            console.error("Errore nel caricamento stagioni:", error);
            notify("Errore nel caricamento stagioni", { type: 'error' });
            setStagioni([]);
        } finally {
            setLoadingStagioni(false);
        }
    };

    const loadGiornateByLegaStagione = async (legaId, stagioneId) => {
        try {
            setLoadingGiornate(true);
            const jwt = getJwt();
            const response = await axios.get(`${host}/proc-get-giornata-elenco`, {
                headers: { 'Authorization': `Bearer ${jwt}` },
                params: {
                    p_input_legaId_dal: legaId,
                    p_input_legaId_al: legaId,
                    p_input_stagioneId_dal: stagioneId,
                    p_input_stagioneId_al: stagioneId
                }
            });

            const data = response.data;
            const giornateArray = Array.isArray(data.data) ? data.data :
                Array.isArray(data) ? data : [];

            setGiornate(giornateArray);
        } catch (error) {
            console.error("Errore nel caricamento giornate:", error);
            notify("Errore nel caricamento giornate", { type: 'error' });
            setGiornate([]);
        } finally {
            setLoadingGiornate(false);
        }
    };

    // Costruisce query per le partite di una squadra specifica (AGGIORNATA)
    const buildQuery = () => {
        const query = {
            $limit: limit,
            $skip: page * limit,
            $or: [
                { teamCasaId: parseInt(teamId) },
                { teamOspiteId: parseInt(teamId) }
            ]
        };

        // Ordinamento
        if (sortConfig.key && COLUMN_CONFIG[sortConfig.key]?.sortable) {
            query.$sort = {
                [sortConfig.key]: sortConfig.direction === 'asc' ? 1 : -1
            };
        }

        // Filtri aggiuntivi
        const filters = {};

        // Ricerca testuale
        if (searchTerm.trim()) {
            const search = `%${searchTerm.trim()}%`;
            // Cerca nell'avversario (sia casa che trasferta)
            query.$or = [
                {
                    teamCasaId: parseInt(teamId),
                    teamOspiteNome: { $like: search }
                },
                {
                    teamOspiteId: parseInt(teamId),
                    teamCasaNome: { $like: search }
                }
            ];
        }

        // Filtro tipo (casa/trasferta)
        if (filterType !== 'all') {
            if (filterType === 'home') {
                query.$or = [{ teamCasaId: parseInt(teamId) }];
            } else if (filterType === 'away') {
                query.$or = [{ teamOspiteId: parseInt(teamId) }];
            }
        }

        // NUOVI FILTRI CASCADE
        if (filterLega !== 'all') filters.legaId = parseInt(filterLega);
        if (filterStagione !== 'all') filters.stagioneId = parseInt(filterStagione);
        if (filterGiornata !== 'all') filters.giornataId = parseInt(filterGiornata);

        // Filtro stato
        if (filterStatus !== 'all') {
            switch (filterStatus) {
                case 'finished':
                    filters.flDisputata = true;
                    break;
                case 'scheduled':
                    filters.flDisputata = false;
                    filters.flAnnullato = false;
                    break;
                case 'cancelled':
                    filters.flAnnullato = true;
                    break;
            }
        }

        // Aggiungi filtri alla query
        Object.assign(query, filters);

        return query;
    };

    // Carica le partite (invariata)
    const loadMatches = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();

            const query = buildQuery();

            const response = await axios.get(
                `${host}/v-partita`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: query
                }
            );

            const data = response.data;
            setMatches(data.data || []);
            setTotal(data.total || 0);

        } catch (error) {
            console.error("Errore nel recupero partite:", error);
            setError("Errore nel caricamento delle partite");
            notify("Errore nel caricamento delle partite", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Handlers (aggiornati)
    const handleColumnToggle = (columnKey) => {
        setVisibleColumns(prev => {
            if (prev.includes(columnKey)) {
                return prev.filter(key => key !== columnKey);
            } else {
                return [...prev, columnKey];
            }
        });
    };

    const handleSort = (columnKey) => {
        if (!COLUMN_CONFIG[columnKey]?.sortable) return;

        setSortConfig(prev => ({
            key: columnKey,
            direction: prev.key === columnKey && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleLimitChange = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const resetFilters = () => {
        setSearchTerm('');
        setFilterStatus('all');
        setFilterType('all');
        // RESET NUOVI FILTRI
        setFilterLega('all');
        setFilterStagione('all');
        setFilterGiornata('all');
        setStagioni([]);
        setGiornate([]);
        setPage(0);
    };

    const resetToDefault = () => {
        setVisibleColumns(['data', 'avversario', 'casa_trasferta', 'risultato', 'stato']);
        setAnchorEl(null);
    };

    const exportToCSV = () => {
        const headers = visibleColumns
            .filter(key => COLUMN_CONFIG[key])
            .map(key => COLUMN_CONFIG[key].label)
            .join(',');

        const rows = matches.map(match => {
            const isHome = match.teamCasaId === parseInt(teamId);
            const opponent = isHome ? match.teamOspiteNome : match.teamCasaNome;

            return visibleColumns
                .filter(key => COLUMN_CONFIG[key])
                .map(key => {
                    let value = '';
                    switch (key) {
                        case 'data':
                            value = match.data ? new Date(match.data).toLocaleDateString('it-IT') : '';
                            break;
                        case 'avversario':
                            value = opponent || '';
                            break;
                        case 'casa_trasferta':
                            value = isHome ? 'Casa' : 'Trasferta';
                            break;
                        case 'risultato':
                            const myScore = isHome ? match.goalCasa : match.goalOspite;
                            const opponentScore = isHome ? match.goalOspite : match.goalCasa;
                            value = (myScore !== null && opponentScore !== null && match.flDisputata) ? `${myScore}-${opponentScore}` : '';
                            break;
                        case 'stato':
                            if (match.flAnnullato) value = 'Annullata';
                            else if (match.flDisputata) value = 'Disputata';
                            else value = 'Programmata';
                            break;
                        case 'lega':
                            value = match.legaNome || `Lega ${match.legaId}`;
                            break;
                        case 'stagione':
                            value = match.stagioneNome || `Stagione ${match.stagioneId}`;
                            break;
                        default:
                            value = match[key] || '';
                    }
                    return `"${value}"`;
                }).join(',');
        }).join('\n');

        const csv = headers + '\n' + rows;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `partite_${teamName || `squadra_${teamId}`}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Auto refresh
    React.useEffect(() => {
        if (autoRefresh) {
            const interval = setInterval(() => {
                loadMatches();
            }, 30000);
            setRefreshInterval(interval);
        } else {
            if (refreshInterval) {
                clearInterval(refreshInterval);
                setRefreshInterval(null);
            }
        }

        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [autoRefresh]);

    // Carica dati quando cambiano filtri, paginazione o ordinamento (AGGIORNATO)
    React.useEffect(() => {
        if (teamId) {
            loadMatches();
        }
    }, [teamId, page, limit, sortConfig, searchTerm, filterStatus, filterType, filterLega, filterStagione, filterGiornata]);

    // Carica leghe all'avvio
    React.useEffect(() => {
        loadLeghe();
    }, []);

    // Salva configurazione colonne
    React.useEffect(() => {
        localStorage.setItem('team-matches-visible-columns', JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    // Calcola statistiche (aggiornate)
    const stats = {
        totali: total,
        casa: matches.filter(m => m.teamCasaId === parseInt(teamId)).length,
        trasferta: matches.filter(m => m.teamOspiteId === parseInt(teamId)).length,
        vinte: matches.filter(m => {
            const isHome = m.teamCasaId === parseInt(teamId);
            const myScore = isHome ? m.goalCasa : m.goalOspite;
            const opponentScore = isHome ? m.goalOspite : m.goalCasa;
            return myScore !== null && opponentScore !== null && myScore > opponentScore;
        }).length,
        pareggiate: matches.filter(m => {
            const isHome = m.teamCasaId === parseInt(teamId);
            const myScore = isHome ? m.goalCasa : m.goalOspite;
            const opponentScore = isHome ? m.goalOspite : m.goalCasa;
            return myScore !== null && opponentScore !== null && myScore === opponentScore;
        }).length,
        perse: matches.filter(m => {
            const isHome = m.teamCasaId === parseInt(teamId);
            const myScore = isHome ? m.goalCasa : m.goalOspite;
            const opponentScore = isHome ? m.goalOspite : m.goalCasa;
            return myScore !== null && opponentScore !== null && myScore < opponentScore;
        }).length,
        // NUOVE STATISTICHE
        leghe: [...new Set(matches.map(m => m.legaId))].length,
        stagioni: [...new Set(matches.map(m => m.stagioneId))].length
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    Partite di {teamName || `Squadra ${teamId}`}
                </Typography>
                {/*  <Typography variant="body2" color="text.secondary">
                    Tutte le partite della squadra con filtri avanzati
                </Typography> */}
            </Box>

            {/* Controlli superiori - AGGIORNATI */}
            <Card sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: 2 }}>
                <Grid container spacing={2} alignItems="center">

                    {/* Ricerca */}
                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Cerca avversario..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    {/* NUOVO Filtro Lega */}
                    <Grid item xs={6} md={1.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Lega</InputLabel>
                            <Select
                                value={filterLega}
                                label="Lega"
                                onChange={(e) => handleLegaChange(e.target.value)}
                            >
                                <MenuItem value="all">Tutte</MenuItem>
                                {leghe.map(lega => (
                                    <MenuItem key={lega.id} value={lega.id}>
                                        {lega.nome || `Lega ${lega.id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* NUOVO Filtro Stagione */}
                    <Grid item xs={6} md={1.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Stagione</InputLabel>
                            <Select
                                value={filterStagione}
                                label="Stagione"
                                onChange={(e) => handleStagioneChange(e.target.value)}
                                disabled={filterLega === 'all' || loadingStagioni}
                            >
                                <MenuItem value="all">
                                    {filterLega === 'all' ? 'Seleziona prima una lega' : 'Tutte'}
                                </MenuItem>
                                {Array.isArray(stagioni) && stagioni.map(stagione => (
                                    <MenuItem key={stagione.id} value={stagione.id}>
                                        {stagione.nomeComposito || stagione.nome || `Stagione ${stagione.id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                            {loadingStagioni && (
                                <Box sx={{ position: 'absolute', right: 32, top: '50%', transform: 'translateY(-50%)' }}>
                                    <CircularProgress size={16} />
                                </Box>
                            )}
                        </FormControl>
                    </Grid>

                    {/* NUOVO Filtro Giornata */}
                    <Grid item xs={6} md={1.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Giornata</InputLabel>
                            <Select
                                value={filterGiornata}
                                label="Giornata"
                                onChange={(e) => handleGiornataChange(e.target.value)}
                                disabled={filterLega === 'all' || filterStagione === 'all' || loadingGiornate}
                            >
                                <MenuItem value="all">
                                    {filterLega === 'all' || filterStagione === 'all' ?
                                        'Seleziona lega e stagione' : 'Tutte'}
                                </MenuItem>
                                {Array.isArray(giornate) && giornate.map(giornata => (
                                    <MenuItem key={giornata.id} value={giornata.id}>
                                        {giornata.nomeComposito || giornata.nome || `Giornata ${giornata.numero || giornata.id}`}
                                    </MenuItem>
                                ))}
                            </Select>
                            {loadingGiornate && (
                                <Box sx={{ position: 'absolute', right: 32, top: '50%', transform: 'translateY(-50%)' }}>
                                    <CircularProgress size={16} />
                                </Box>
                            )}
                        </FormControl>
                    </Grid>

                    {/* Filtro Casa/Trasferta */}
                    <Grid item xs={6} md={1.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Tipo</InputLabel>
                            <Select
                                value={filterType}
                                label="Tipo"
                                onChange={(e) => {
                                    setFilterType(e.target.value);
                                    setPage(0);
                                }}
                            >
                                <MenuItem value="all">Tutte</MenuItem>
                                <MenuItem value="home">Casa</MenuItem>
                                <MenuItem value="away">Trasferta</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Filtro Stato */}
                    <Grid item xs={6} md={1.5}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Stato</InputLabel>
                            <Select
                                value={filterStatus}
                                label="Stato"
                                onChange={(e) => {
                                    setFilterStatus(e.target.value);
                                    setPage(0);
                                }}
                            >
                                <MenuItem value="all">Tutti</MenuItem>
                                <MenuItem value="finished">Disputate</MenuItem>
                                <MenuItem value="scheduled">Programmate</MenuItem>
                                <MenuItem value="cancelled">Annullate</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Auto refresh */}
                    <Grid item xs={6} md={1}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                    size="small"
                                />
                            }
                            label="Auto"
                            sx={{ margin: 0 }}
                        />
                    </Grid>

                    {/* Controlli */}
                    <Grid item xs={6} md={1.5}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Tooltip title="Cancella filtri">
                                <IconButton
                                    size="small"
                                    onClick={resetFilters}
                                    disabled={searchTerm === '' && filterType === 'all' && filterStatus === 'all' && filterLega === 'all' && filterStagione === 'all' && filterGiornata === 'all'}
                                >
                                    <ClearIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Esporta CSV">
                                <IconButton
                                    size="small"
                                    onClick={exportToCSV}
                                    disabled={!matches.length}
                                >
                                    <GetAppIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title="Configura colonne">
                                <IconButton
                                    size="small"
                                    onClick={(e) => setAnchorEl(e.currentTarget)}
                                >
                                    <Badge badgeContent={visibleColumns.length} color="primary">
                                        <SettingsIcon />
                                    </Badge>
                                </IconButton>
                            </Tooltip>

                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                                onClick={loadMatches}
                                disabled={loading}
                                sx={{ textTransform: 'none' }}
                            >
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Card>

            {/* Menu configurazione colonne */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                PaperProps={{
                    sx: { maxHeight: 500, width: 280 }
                }}
            >
                <MenuItem onClick={resetToDefault}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Reset a default
                    </Typography>
                </MenuItem>
                <Divider />
                {Object.entries(COLUMN_CONFIG).map(([key, config]) => (
                    <MenuItem key={key} onClick={() => handleColumnToggle(key)}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={visibleColumns.includes(key)}
                                    size="small"
                                />
                            }
                            label={config.label}
                            sx={{ margin: 0, width: '100%' }}
                        />
                    </MenuItem>
                ))}
            </Menu>

            {/* Statistiche rapide - AGGIORNATE */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={1.5}>
                    <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#4A90E2' }}>
                            {stats.totali}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Totali
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={6} md={1.5}>
                    <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>
                            {stats.vinte}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Vinte
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={6} md={1.5}>
                    <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>
                            {stats.pareggiate}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Pareggiate
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={6} md={1.5}>
                    <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#f44336' }}>
                            {stats.perse}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Perse
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={6} md={1.5}>
                    <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                            {stats.casa}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Casa
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={6} md={1.5}>
                    <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#e65100' }}>
                            {stats.trasferta}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Trasferta
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={6} md={1.5}>
                    <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1565c0' }}>
                            {stats.leghe}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Leghe
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={6} md={1.5}>
                    <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#9c27b0' }}>
                            {stats.stagioni}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Stagioni
                        </Typography>
                    </Card>
                </Grid>
            </Grid>

            {/* Tabella partite */}
            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading && !matches.length ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                        <CircularProgress size={40} />
                    </Box>
                ) : matches.length > 0 ? (
                    <>
                        <TableContainer sx={{ maxHeight: 600 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {visibleColumns
                                            .filter(columnKey => COLUMN_CONFIG[columnKey])
                                            .map(columnKey => {
                                                const config = COLUMN_CONFIG[columnKey];
                                                return (
                                                    <TableCell
                                                        key={columnKey}
                                                        align={config.align}
                                                        sx={{
                                                            fontWeight: 600,
                                                            width: config.width,
                                                            cursor: config.sortable ? 'pointer' : 'default',
                                                            bgcolor: sortConfig.key === columnKey ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                                                            '&:hover': config.sortable ? {
                                                                bgcolor: 'rgba(0, 0, 0, 0.04)'
                                                            } : {},
                                                            borderBottom: '2px solid #e0e0e0'
                                                        }}
                                                        onClick={() => handleSort(columnKey)}
                                                    >
                                                        {config.label}
                                                        {sortConfig.key === columnKey && (
                                                            <span style={{ marginLeft: 4 }}>
                                                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {matches.map((match, index) => (
                                        <TableRow
                                            key={match.partitaId || index}
                                            sx={{
                                                '&:hover': {
                                                    bgcolor: 'rgba(0, 0, 0, 0.04)'
                                                },
                                                bgcolor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'inherit',
                                                opacity: match.flAnnullato ? 0.6 : 1
                                            }}
                                        >
                                            {visibleColumns
                                                .filter(columnKey => COLUMN_CONFIG[columnKey])
                                                .map(columnKey => {
                                                    const config = COLUMN_CONFIG[columnKey];
                                                    const value = match[config.key];

                                                    return (
                                                        <TableCell
                                                            key={columnKey}
                                                            align={config.align}
                                                            sx={{
                                                                width: config.width,
                                                                py: 1
                                                            }}
                                                        >
                                                            {config.render ?
                                                                config.render(value, match, index) :
                                                                value || '-'
                                                            }
                                                        </TableCell>
                                                    );
                                                })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Paginazione */}
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={handlePageChange}
                            rowsPerPage={limit}
                            onRowsPerPageChange={handleLimitChange}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelRowsPerPage="Righe per pagina:"
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
                            }
                            sx={{
                                borderTop: '1px solid #e0e0e0',
                                '& .MuiTablePagination-toolbar': {
                                    px: 2
                                }
                            }}
                        />
                    </>
                ) : (
                    <Box sx={{ textAlign: 'center', p: 6 }}>
                        <CalendarTodayIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            {searchTerm || filterType !== 'all' || filterStatus !== 'all' || filterLega !== 'all' || filterStagione !== 'all' || filterGiornata !== 'all' ?
                                'Nessuna partita trovata con i filtri applicati' :
                                'Nessuna partita presente per questa squadra'
                            }
                        </Typography>
                        {(searchTerm || filterType !== 'all' || filterStatus !== 'all' || filterLega !== 'all' || filterStagione !== 'all' || filterGiornata !== 'all') && (
                            <Button
                                variant="outlined"
                                onClick={resetFilters}
                                sx={{ mt: 2 }}
                            >
                                Cancella filtri
                            </Button>
                        )}
                    </Box>
                )}
            </Card>

            {/* Footer */}
            {matches.length > 0 && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Ultimo aggiornamento: {new Date().toLocaleString('it-IT')}
                        {autoRefresh && " • Auto refresh attivo"}
                        • Pagina {page + 1} di {Math.ceil(total / limit)}
                        {(searchTerm || filterType !== 'all' || filterStatus !== 'all' || filterLega !== 'all' || filterStagione !== 'all' || filterGiornata !== 'all') &&
                            ` • Filtri applicati`
                        }
                    </Typography>
                </Box>
            )}
        </Box>
    );
};